import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { money } from "@/lib/api";
import { PageHeader, StatCard } from "@/components/ui-bits";
import { ExitSettlement } from "@/admin/ExitSettlement";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  ArrowLeft, Loader2, Coins, ArrowUpRight, Package, Banknote, TrendingUp,
} from "lucide-react";

const STATUS = { paid: "default", partial: "secondary", pending: "outline" };
const TIER_LABELS = ["Base", "+10%", "+20%", "+30%"];

function monthName(m) {
  const [y, mo] = m.split("-");
  return new Date(y, mo - 1).toLocaleString(undefined, { month: "short", year: "numeric" });
}

export default function AgentDetail() {
  const { agentId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  const load = useCallback(() =>
    api.get(`/admin/agents/${agentId}/detail`)
      .then((r) => setData(r.data))
      .catch(() => setErr("Could not load agent details.")), [agentId]);
  useEffect(() => { load(); }, [load]);

  if (err) return <div className="py-16 text-center text-muted-foreground">{err}</div>;
  if (!data)
    return <div className="flex justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  const a = data.agent;
  const t = data.totals;
  const localSym = data.symbol;
  const cur = data.currency;

  return (
    <div>
      <button
        onClick={() => navigate("/dashboard/agents")}
        className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-4"
        data-testid="agent-detail-back"
      >
        <ArrowLeft className="w-4 h-4" /> Back to agents
      </button>

      <PageHeader
        title={a.name}
        subtitle={`${a.agent_code || "—"} · ${a.country} · paid in ${cur} · ${a.email}`}
        actions={<Badge variant={a.status === "active" ? "default" : "outline"}>{a.status}</Badge>}
      />

      <ExitSettlement agentId={agentId} onChanged={load} />

      {/* KPI cards (AED base) */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Paid sales value" value={money(t.sales_value)} icon={Banknote} />
        <StatCard label="Base commission" value={money(t.base_commission)} icon={Coins} accent />
        <StatCard label="Above-list bonus" value={money(t.above_bonus)} icon={ArrowUpRight} accent />
        <StatCard label="Bags sold (paid)" value={t.bags} icon={Package} />
      </div>

      {/* Monthly earnings summary */}
      <Card className="p-5 mt-4">
        <h3 className="font-display font-semibold mb-4 flex items-center gap-2">
          <TrendingUp className="w-4 h-4 text-primary" /> Monthly earnings summary
        </h3>
        {data.monthly.length === 0 ? (
          <p className="text-sm text-muted-foreground">No earnings yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
                  <th className="text-left font-medium py-2">Month</th>
                  <th className="text-right font-medium py-2">Base comm.</th>
                  <th className="text-right font-medium py-2">Accelerator</th>
                  <th className="text-right font-medium py-2">Above-list</th>
                  <th className="text-right font-medium py-2">New-cust.</th>
                  <th className="text-right font-medium py-2">Deduct</th>
                  <th className="text-right font-medium py-2">Total (AED)</th>
                  <th className="text-right font-medium py-2">In {cur}</th>
                </tr>
              </thead>
              <tbody>
                {data.monthly.map((m) => (
                  <tr key={m.month} className="border-b border-border/40" data-testid={`agent-month-${m.month}`}>
                    <td className="py-2 font-medium">{monthName(m.month)} <span className="text-xs text-muted-foreground">· {TIER_LABELS[m.accelerator_tier_index]}</span></td>
                    <td className="py-2 text-right">{money(m.base_commission)}</td>
                    <td className="py-2 text-right">{money(m.accelerator_bonus)}</td>
                    <td className="py-2 text-right">{money(m.above_price_bonus)}</td>
                    <td className="py-2 text-right">{money(m.new_customer_bonus)}</td>
                    <td className="py-2 text-right text-destructive">{m.deductions ? `−${money(m.deductions)}` : "—"}</td>
                    <td className="py-2 text-right font-display font-bold">{money(m.total)}</td>
                    <td className="py-2 text-right text-muted-foreground">{money(m.total_local, cur, localSym)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Above-list premium (separate) */}
      <Card className="p-5 mt-4">
        <h3 className="font-display font-semibold mb-1 flex items-center gap-2">
          <ArrowUpRight className="w-4 h-4 text-accent" /> Above-list price sales & extra commission
        </h3>
        <p className="text-xs text-muted-foreground mb-4">
          When sold above list price, the agent keeps {Math.round(data.above_split * 100)}% of every extra dirham collected — shown here sale-wise, date-wise.
        </p>
        {data.above_list_sales.length === 0 ? (
          <p className="text-sm text-muted-foreground">No above-list-price sales for this agent.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
                  <th className="text-left font-medium py-2">Date</th>
                  <th className="text-left font-medium py-2">Customer</th>
                  <th className="text-left font-medium py-2">Product</th>
                  <th className="text-right font-medium py-2">Qty</th>
                  <th className="text-right font-medium py-2">+Above/bag</th>
                  <th className="text-right font-medium py-2">Extra collected</th>
                  <th className="text-right font-medium py-2">Extra commission</th>
                </tr>
              </thead>
              <tbody>
                {data.above_list_sales.map((s, i) => (
                  <tr key={`${s.order_id}-${s.product_id || i}`} className="border-b border-border/40" data-testid={`above-sale-${i}`}>
                    <td className="py-2 text-muted-foreground">{s.date}</td>
                    <td className="py-2">{s.customer}</td>
                    <td className="py-2">{s.product}</td>
                    <td className="py-2 text-right">{s.qty}</td>
                    <td className="py-2 text-right">{money(s.above_per_bag)}</td>
                    <td className="py-2 text-right">{money(s.above_extra)}</td>
                    <td className="py-2 text-right font-semibold text-accent">{money(s.above_bonus)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* All sales (sale-wise, date-wise) */}
      <Card className="p-5 mt-4">
        <h3 className="font-display font-semibold mb-4 flex items-center gap-2">
          <Coins className="w-4 h-4 text-primary" /> All sales & commission (date-wise)
        </h3>
        {data.sales.length === 0 ? (
          <p className="text-sm text-muted-foreground">No sales recorded.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
                  <th className="text-left font-medium py-2">Invoice</th>
                  <th className="text-left font-medium py-2">Date</th>
                  <th className="text-left font-medium py-2">Customer</th>
                  <th className="text-left font-medium py-2">Product</th>
                  <th className="text-right font-medium py-2">Qty</th>
                  <th className="text-right font-medium py-2">Disc.</th>
                  <th className="text-right font-medium py-2">Sale value</th>
                  <th className="text-right font-medium py-2">Base comm.</th>
                  <th className="text-right font-medium py-2">Above bonus</th>
                  <th className="text-right font-medium py-2">Total comm.</th>
                  <th className="text-left font-medium py-2 pl-3">Payment</th>
                </tr>
              </thead>
              <tbody>
                {data.sales.map((s, i) => (
                  <tr key={`${s.order_id}-${s.product_id || i}`} className="border-b border-border/40 hover:bg-secondary/40" data-testid={`agent-sale-${i}`}>
                    <td className="py-2 font-mono text-xs">{s.invoice_no || "—"}</td>
                    <td className="py-2 text-muted-foreground whitespace-nowrap">{s.date}</td>
                    <td className="py-2">{s.customer}</td>
                    <td className="py-2">{s.product}</td>
                    <td className="py-2 text-right">{s.qty}</td>
                    <td className="py-2 text-right">{s.discount_level}%</td>
                    <td className="py-2 text-right">{money(s.sale_value)}</td>
                    <td className="py-2 text-right">{money(s.base_commission)}</td>
                    <td className="py-2 text-right">{s.above_bonus ? money(s.above_bonus) : "—"}</td>
                    <td className="py-2 text-right font-semibold text-primary">{money(s.total_commission)}</td>
                    <td className="py-2 pl-3">
                      <Badge variant={s.is_return ? "destructive" : STATUS[s.payment_status]} className="capitalize">
                        {s.is_return ? "Return" : s.payment_status}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="text-xs text-muted-foreground mt-3">All amounts in base currency (AED). Commission counts toward payout only when the order is paid in full.</p>
      </Card>
    </div>
  );
}
